import { useCallback, useEffect, useState } from "react";

import { Plus, Users } from "lucide-react";
import { Switch } from "../../components/ui/switch.jsx";
import { Label } from "../../components/ui/label.jsx";

import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { usePagination } from "../../hooks/usePagination";

import VendorDialog from "./component/VendorDialog";
import VendorsTab from "./component/VendorsTab";

const Vendors = () => {
  const { user } = useAuth();
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");
  const [selectedType, setSelectedType] = useState("ALL");
  const [isVendorDialogOpen, setIsVendorDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const vendorPagination = usePagination(10);
  const {
    items: vendors,
    currentPage: vendorPage,
    pageSize: vendorPageSize,
    totalItems: vendorTotalItems,
    setPaginationData: setVendorPaginationData,
    setCurrentPage: setVendorPage
  } = vendorPagination;

  const fetchVendors = useCallback(
    async (page) => {
      try {
        setLoading(true);
        const response = await api.get(`/vendors`, {
          params: {
            page,
            size: vendorPageSize,
            search: vendorSearch.trim() || undefined,
            type: selectedType !== "ALL" ? selectedType : undefined,
            showDeleted
          }
        });
        setVendorPaginationData(response.data);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      } finally {
        setLoading(false);
      }
    },
    [api, vendorPageSize, vendorSearch, selectedType, showDeleted, setVendorPaginationData]
  );

  useEffect(() => {
    fetchVendors(vendorPage);
  }, [vendorPage, vendorPageSize, fetchVendors]);

  const handleAddVendor = () => {
    setEditingVendor(null);
    setIsVendorDialogOpen(true);
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setIsVendorDialogOpen(true);
  };

  if (!user?.permissions?.vendor?.read && user?.role !== "super_admin") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 bg-yale-blue-50 dark:bg-yale-blue-900/50 rounded-xl border border-dashed border-slate-200 dark:border-yale-blue-800">
        <div className="bg-slate-200 dark:bg-yale-blue-800 p-4 rounded-full mb-4">
          <Users className="w-8 h-8 text-slate-400 dark:text-yale-blue-500" />
        </div>
        <h3 className="text-xl font-semibold text-yale-blue-900 dark:text-yale-blue-50 mb-2">Access Restricted</h3>
        <p className="text-yale-blue-500 dark:text-slate-400 max-w-xs mx-auto">
          You don't have permission to view vendors. Please contact your system administrator for access.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-500">
      <div className="flex-1 flex flex-col pb-8">
        <PageHeader
          title="Vendor Management"
          subtitle="Manage your material and service suppliers"
          pageIcon={Users}
          actionLabel={user?.permissions?.vendor?.create ? "Add Vendor" : null}
          onActionClick={handleAddVendor}
          icon={Plus}
          extraActions={
            <div className="flex items-center gap-4">
              <SearchInput
                placeholder="Search vendors..."
                className="w-full md:w-64"
                onSearch={(val) => {
                  setVendorSearch(val);
                  setVendorPage(0);
                }}
                value={vendorSearch}
              />
            </div>
          }
        />

        <div className="flex-1 flex flex-col">
          <VendorsTab
            vendors={vendors}
            fetchVendorData={() => fetchVendors(vendorPage)}
            onEditVendor={handleEditVendor}
            filters={{
              type: selectedType
            }}
            onFilterChange={(filterName, value) => {
              if (filterName === "type") {
                setSelectedType(value);
                setVendorPage(0);
              }
            }}
            showDeleted={showDeleted}
            setShowDeleted={setShowDeleted}
            setVendorPage={setVendorPage}
          />
        </div>

        <div className="mt-auto pt-6">
          <Pagination pagination={vendorPagination} />
        </div>
      </div>

      <VendorDialog
        isOpen={isVendorDialogOpen}
        onOpenChange={setIsVendorDialogOpen}
        onSuccess={() => {
          fetchVendors(vendorPage);
          setEditingVendor(null);
        }}
        editingVendor={editingVendor}
      />
    </div>
  );
};

export default Vendors;
